import "./admin-list-user.scss";

import { Eye, EyeClosed, UserPlus, X } from "lucide-react";
import CustomList from "../../../components/custom-list/custom-list";
import { useAppDispatch, useAppSelector } from "../../../features/helpers";
import { addUser, getAllUsers, getUserList, getUserModalStatus, resetUserPassword, setUserModalStatus, updateUserStatus } from "../../../store/UserSlice";
import { useEffect, useMemo, useState } from "react";
import { getOrganizations, getOrganizationsList } from "../../../store/OrganizationSlice";
import { Roles } from "../../../utils/lists";

const STATUS_OPTIONS = [
    { value: 'active',   label: 'Ativo' },
    { value: 'inactive', label: 'Inativo' },
];

export interface UserAddInfo {
    name:string,
    email:string,
    login:string,
    password?: string,
    confirm?: string,
    role?:string,
    organizationId?: number,
    status?: string,
    fee?: string
}

const UserAddRest = {
    name: '',
    email: '',
    login: '',
    password: '',
    confirm: '',
    role: '',
    organizationId: 0,
}

// Mesma regra do registro publico (PASSWORD_REGEX do backend): min 8 chars,
// com pelo menos uma letra e um digito.
const PASSWORD_REGEX = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/;

const AdminListUser = () => {

    const dispatch = useAppDispatch();

    const roles                     = Object.entries(Roles);
    const modalStatus: boolean      = useAppSelector(getUserModalStatus);
    const userList : Array<any>     = useAppSelector(getUserList);
    const organizationList          = useAppSelector(getOrganizationsList);

    // Filtro por role da lista de usuarios cadastrados.
    const[roleFilter, setRoleFilter] = useState<string>('');

    const toggle = () => {
        dispatch(setUserModalStatus(!modalStatus));
    }

    useEffect( () => {
        dispatch(getAllUsers());
        dispatch(getOrganizations())
    }, [dispatch])

    // Roles disponiveis derivados dos proprios dados + contagem por role.
    const roleCounts = useMemo(() => {
        const acc: Record<string, number> = {};
        (userList ?? []).forEach((u: any) => {
            if (u?.role) acc[u.role] = (acc[u.role] ?? 0) + 1;
        });
        return acc;
    }, [userList]);

    const roleOptions = useMemo(
        () => Object.keys(roleCounts).sort((a, b) => a.localeCompare(b)),
        [roleCounts]
    );

    const filteredUsers = useMemo(
        () => roleFilter ? (userList ?? []).filter((u: any) => u?.role === roleFilter) : (userList ?? []),
        [userList, roleFilter]
    );

    // Reordena as chaves para 'status' ficar por ultimo — o CustomList deriva a
    // ordem das colunas da ordem das chaves do objeto.
    // `usingDefaultPassword` vira a coluna 'senha' (o CustomList renderiza
    // valor cru, e "true"/"false" nao diz nada para quem opera).
    const displayUsers = useMemo(
        () => filteredUsers.map(({ status, usingDefaultPassword, ...rest }: any) => ({
            ...rest,
            senha: usingDefaultPassword ? 'padrao' : 'definida',
            status,
        })),
        [filteredUsers]
    );

    const[addPayload, setAddPayload] = useState<UserAddInfo>(UserAddRest);
    const[formError, setFormError]   = useState<string>('');
    const[showPassword, setShowPassword] = useState<boolean>(false);
    const[showConfirm, setShowConfirm]   = useState<boolean>(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement| HTMLSelectElement>) => {
        const { name, value } = e.target;
        setAddPayload(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAdd = async () => {
        const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addPayload.email);

        // Org e obrigatoria: o create do dashboard (register !== 'true') exige um
        // organizationId numerico valido — senao o backend reprova com 400.
        if (
            !addPayload.name ||
            !addPayload.email ||
            !addPayload.login ||
            !addPayload.role ||
            !addPayload.organizationId ||
            Number(addPayload.organizationId) <= 0
        ) {
            setFormError('Preencha nome, e-mail, login, role e organizacao.');
            return;
        }
        if (!emailValid) {
            setFormError('Informe um e-mail valido.');
            return;
        }
        // A senha e definida aqui de proposito: se ficar em branco o backend
        // gera uma aleatoria que ninguem chega a ver, e o usuario nasce sem
        // acesso (nao ha fluxo de reset de senha).
        if (!addPayload.password) {
            setFormError('Defina uma senha para o usuario.');
            return;
        }
        if (!PASSWORD_REGEX.test(addPayload.password)) {
            setFormError('A senha precisa ter no minimo 8 caracteres, com ao menos uma letra e um numero.');
            return;
        }
        if (addPayload.password !== addPayload.confirm) {
            setFormError('As senhas nao conferem.');
            return;
        }

        // Envia apenas os campos do CreateUserDto (ValidationPipe com
        // forbidNonWhitelisted). 'document' fica de fora — vai pelo endpoint
        // POST /users/document/:id. 'status' e obrigatorio no DTO e 'role' ja
        // carrega o valor do AccesRole (enum Roles alinhado ao backend).
        const payload: UserAddInfo = {
            name: addPayload.name,
            email: addPayload.email,
            login: addPayload.login,
            password: addPayload.password,
            role: addPayload.role,
            organizationId: Number(addPayload.organizationId),
            status: 'active',
        };

        setFormError('');
        const created = await dispatch(addUser(payload));
        if (created) setAddPayload(UserAddRest);
        else setFormError('Nao foi possivel criar o usuario. Tente novamente.');
    }

    const handleStatusChange = (id: number, status: string) => {
        dispatch(updateUserStatus(id, status));
    }

    // O retorno (sucesso ou falha) aparece pelo toast, disparado dentro do thunk.
    const handleResetPassword = (id: number) => {
        dispatch(resetUserPassword(id));
    }

    return (
        <>
            <div className="admin-add-user">
                <div className="header-info">
                    <h1 className="page-title">
                        Usuarios
                        <small>lista de usuarios, cargos e opcao de adicionar mais.</small>
                    </h1>
                    <div className="btn purple" onClick={toggle}>
                        <UserPlus size={20} />
                        Adicionar
                    </div>
                </div>
                { userList && (
                    <>
                        <div className="role-filters">
                            <button
                                className={`role-chip ${roleFilter === '' ? 'active' : ''}`}
                                onClick={() => setRoleFilter('')}
                            >
                                Todos <span className="count">{userList.length}</span>
                            </button>
                            { roleOptions.map((role) => (
                                <button
                                    key={role}
                                    className={`role-chip ${roleFilter === role ? 'active' : ''}`}
                                    onClick={() => setRoleFilter(role)}
                                >
                                    {role} <span className="count">{roleCounts[role]}</span>
                                </button>
                            ))}
                        </div>

                        <div className="admin-custom-list">
                            { filteredUsers.length > 0
                                ? <CustomList
                                    data={displayUsers}
                                    onStatusChange={handleStatusChange}
                                    statusOptions={STATUS_OPTIONS}
                                    onResetPassword={handleResetPassword}
                                  />
                                : <p className="empty-list">Nenhum usuario para esse filtro.</p> }
                        </div>
                    </>
                )}
            </div>

            <div className={`modal ${!modalStatus ? 'hide' : ''}`}>
                <div className="shadow"></div>
                <div className="modal-body form">
                    <h3 className="modal-title">
                        <div>
                            Add Usuario/Cliente
                            <small>Modal para insercao de usuarios ou clientes.</small>
                        </div>
                        <div className="buttons">
                            <X size={40} onClick={toggle}/>
                        </div>
                    </h3>
                    <div className="form-group">
                        <label htmlFor="name">Nome</label>
                        <input type="text" name="name" value={addPayload.name} onChange={handleChange} placeholder="Nome do Usuario"/>
                    </div>
                    <div className="form-group">
                        <label htmlFor="email">E-mail</label>
                        <input type="text" name="email" value={addPayload.email} onChange={handleChange} placeholder="E-mail de contato do usuario"/>
                    </div>
                    <div className="form-group">
                        <label htmlFor="login">login (usado para login)</label>
                        <input type="text" name="login" value={addPayload.login} onChange={handleChange} placeholder="nick usado para login" />
                    </div>
                    <div className="form-group password-field">
                        <label htmlFor="password">Senha</label>
                        <input
                            type={showPassword ? "text" : "password"}
                            id="password"
                            name="password"
                            autoComplete="new-password"
                            value={addPayload.password ?? ''}
                            onChange={handleChange}
                            placeholder="Min. 8 caracteres, com letra e numero"
                        />
                        { showPassword
                            ? <Eye       className="toggle" size={18} onClick={() => setShowPassword(false)} />
                            : <EyeClosed className="toggle" size={18} onClick={() => setShowPassword(true)} /> }
                    </div>
                    <div className="form-group password-field">
                        <label htmlFor="confirm">Confirme a senha</label>
                        <input
                            type={showConfirm ? "text" : "password"}
                            id="confirm"
                            name="confirm"
                            autoComplete="new-password"
                            value={addPayload.confirm ?? ''}
                            onChange={handleChange}
                            placeholder="Digite a senha novamente"
                        />
                        { showConfirm
                            ? <Eye       className="toggle" size={18} onClick={() => setShowConfirm(false)} />
                            : <EyeClosed className="toggle" size={18} onClick={() => setShowConfirm(true)} /> }
                    </div>
                    <div className="form-group">
                        <label>Organizacoes</label>
                        <select name="organizationId" value={addPayload.organizationId ?? 0} onChange={handleChange}>
                            <option value={0}>Selecione a Organizacao</option>
                            { organizationList &&  organizationList.map( (org, index) => (
                                <option value={org.id} key={index}>{org.nome}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Role (papel desempenhado)</label>
                        <select onChange={handleChange} name='role' value={addPayload.role ?? ''}>
                            <option value="">Selecione o Role</option>
                            {roles.map(([key, value]) => (
                                <option key={key} value={value}>
                                    {value}
                                </option>
                            ))}
                        </select>
                    </div>

                    { formError && <span className="text-danger">{formError}</span> }

                    <div className="buttons">
                        <div className="btn purple" onClick={handleAdd}>
                            Adicionar
                        </div>
                    </div>

                </div>
            </div>
        </>

    )
}

export default AdminListUser;